/**
 * Emergency contact contract — mirrors `platform.employee_emergency_contacts`.
 *
 * An employee can have many contacts but at most one flagged `isPrimary`
 * (partial unique index on the DB side). The service layer demotes an
 * existing primary before promoting a new one, rather than rejecting the
 * insert — callers shouldn't have to orchestrate two requests.
 */
import { z } from 'zod';

import { TenantIdSchema } from '../common/identifiers.js';
import { AuditFieldsSchema } from '../common/timestamps.js';

import { EmployeeIdSchema } from './employee.js';

export const EmergencyContactIdSchema = z.string().uuid().brand<'EmergencyContactId'>();
export type EmergencyContactId = z.infer<typeof EmergencyContactIdSchema>;

const EmailLikeSchema = z.string().email().max(320);

export const EmployeeEmergencyContactSchema = AuditFieldsSchema.extend({
  id: EmergencyContactIdSchema,
  tenantId: TenantIdSchema,
  employeeId: EmployeeIdSchema,
  name: z.string().min(1).max(200),
  relationship: z.string().min(1).max(80),
  phone: z.string().min(1).max(32),
  email: EmailLikeSchema.nullable(),
  isPrimary: z.boolean(),
});
export type EmployeeEmergencyContact = z.infer<typeof EmployeeEmergencyContactSchema>;

/** Input for `POST /employees/:id/emergency-contacts`. */
export const NewEmployeeEmergencyContactSchema = z.object({
  name: z.string().min(1).max(200),
  relationship: z.string().min(1).max(80),
  phone: z.string().min(1).max(32),
  email: EmailLikeSchema.nullable().optional(),
  isPrimary: z.boolean().optional(),
});
export type NewEmployeeEmergencyContact = z.infer<typeof NewEmployeeEmergencyContactSchema>;

/** Input for `PATCH /employees/:id/emergency-contacts/:contactId`. */
export const UpdateEmployeeEmergencyContactSchema = NewEmployeeEmergencyContactSchema.partial();
export type UpdateEmployeeEmergencyContact = z.infer<typeof UpdateEmployeeEmergencyContactSchema>;
